<template>
  <card-dialog
    v-model="modalVisible"
    max-width="400px"
    :title="device ? $t('edit_device.dialog.header') : $t('new_device.dialog.header')"
  >
    <v-card-text
      class="pa-6"
    >
      <validation-observer ref="validation">
        <v-row no-gutters>
          <v-col>
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-text-field
                v-model="deviceFormData.serial_number"
                :error-messages="errors"
                :label="$t('table_headers.admin_devices.serial_number')"
                dense
                outlined
                readonly
                :disabled="!!deviceFormData.id"
              />
            </validation-provider>
          </v-col>
          <v-col
            v-if="!deviceFormData.id"
            cols="auto"
            class="pl-2"
          >
            <v-btn
              color="primary"
              class="text-none"
              @click="loadIdentifier()"
            >
              {{ $t('button.load_from_usb') }}
            </v-btn>
          </v-col>
        </v-row>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="deviceFormData.device_name"
            :error-messages="errors"
            :label="$t('text_input.device_name')"
            dense
            outlined
            :disabled="!!deviceFormData.id"
            persistent-hint
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="deviceFormData.human_id"
            :error-messages="errors"
            :label="$t('text_input.custom_name.title')"
            dense
            outlined
            :placeholder="$t('text_input.custom_name.placeholder')"
            persistent-hint
            :hint="$t('text_input.custom_name.hint')"
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
        >
          <v-autocomplete
            :key="modalVisible"
            v-model="selectedPlayer"
            :loading="players.loading"
            :items="players.data"
            :error-messages="errors"
            dense
            outlined
            return-object
            item-text="last_name"
            :item-value="item => item.id"
            :filter="(item, queryText) => {
              return (item.first_name + item.last_name).toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
            }"
            :placeholder="$t('dropdown.associated_player.placeholder')"
            :label="$t('dropdown.associated_player.title')"
            @input="addPlayer"
          >
            <template v-slot:selection="{ item }">
              <span>{{ item.first_name }} {{ item.last_name }}</span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="item.teams && item.teams.length">{{ item.teams.map(t => t.short_name).join(', ') }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  >&middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <draggable
            v-model="deviceFormData.players"
            group="players"
            handle=".handle"
            @change="updatePlayerOrder"
          >
            <v-list-item
              v-for="(player, index) in deviceFormData.players"
              :key="player.id"
            >
              <v-list-item-icon
                class="handle"
                style="cursor: move"
              >
                <v-icon>far fa-grip-lines</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title>
                  {{ player.name || getPlayerName(player.id) }}
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  icon
                  small
                  @click="removePlayer(index)"
                >
                  <v-icon small>
                    far fa-times
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </draggable>
        </validation-provider>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            @click="submit()"
          >
            {{ $t('training_builder.button.save') }}
          </v-btn>
          <v-btn
            v-if="deviceFormData.id"
            color="error"
            text
            block
            class="mt-1"
            @click="toggleConfirmationDialog(true)"
          >
            {{ $t('button.delete') }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteDevice()"
    />
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { DeviceApiService } from '@/data/services/DeviceApiService'
import { PlayerApiService } from '@/data/services'
import draggable from 'vuedraggable'
import { cloneDeep } from 'lodash'

const emptyDeviceFormData = {
  id: undefined,
  serial_number: '',
  device_name: '',
  human_id: '',
  players: []
}

export default {
  name: 'DeviceFormDialog',
  components: {
    DeleteConfirmationDialog,
    CardDialog,
    draggable
  },
  mixins: [deleteConfirmationMixin],
  props: {
    device: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      deviceFormData: emptyDeviceFormData,
      submitting: false,
      loadingDeviceId: false,
      players: {
        data: [],
        loading: false
      },
      selectedPlayer: null
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    }
  },
  created() {
    EventBus.$on(this.$events.OPEN_DIALOG_DEVICE, () => {
      this.selectedPlayer = null
      this.modalVisible = true
      this.deviceFormData = {
        id: undefined,
        serial_number: '',
        device_name: '',
        human_id: '',
        players: []
      }

      this.fetchAllPlayers()
    })

    EventBus.$on(this.$events.EDIT_DEVICE, (device) => {
      this.deviceFormData = {
        id: device.id,
        serial_number: device.serial_number,
        device_name: device.device_name,
        human_id: device.human_id,
        players: cloneDeep(device.players)
      }

      this.modalVisible = true

      this.selectedPlayer = null
      this.fetchAllPlayers()
    })
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          serial_number: this.deviceFormData.serial_number,
          device_name: this.deviceFormData.device_name,
          human_id: this.deviceFormData.human_id,
          players: this.deviceFormData.players.map(p => {
            return {
              id: p.id,
              priority: p.priority
            }
          })
        }

        let result

        if (this.deviceFormData.id) {
          data.id = this.deviceFormData.id

          result = await DeviceApiService.update(data)
        } else {
          result = await DeviceApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.device_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.deviceFormData.id) {
          EventBus.$emit(this.$events.DEVICE_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.DEVICE_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.device_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteDevice() {
      try {
        this.deleting = true

        await DeviceApiService.delete(this.deviceFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.device_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.DEVICE_DELETED, this.deviceFormData)

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.device_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async loadIdentifier() {
      this.loadingDeviceId = true
      const USB_ACK = 0x0A
      const USB_CMD_POD_GET_ID = 0x11

      if (window.navigator.userAgent.indexOf('Chrome') === -1) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          color: 'error',
          text: 'Works only with Google Chrome!'
        })
        this.loadingDeviceId = false
        return
      }

      try {
        const device = await navigator.usb.requestDevice({
          filters: [{
            vendorId: 0x1915
          }]
        })

        if (device === undefined) return

        await device.open()
        if (device.configuration === null) {
          await device.selectConfiguration(1)
        }
        await device.claimInterface(0)

        /* ----variable required---- */
        const cmdID = new Uint8Array(1).fill(USB_CMD_POD_GET_ID) // create an array and store the cmd ID.
        /* ------------------------- */
        await device.transferOut(1, cmdID)
        const ack = await device.transferIn(1, 1)
        if (ack.data.getUint8(0) !== USB_ACK) {
          return
        }
        const udidBuff = await device.transferIn(1, 8)
        let str = String('')
        for (let i = 0; i < 8; i++) {
          const myNumber = udidBuff.data.getUint8(i)
          const myNumberAsSTR = myNumber.toString(16)
          const formattedNumber = ('0' + myNumberAsSTR).slice(-2)
          str = str + formattedNumber
        }

        this.deviceFormData.serial_number = str
      } catch (err) {
        // No device was selected.
        // statusTxt.innerHTML = err;
      } finally {
        this.loadingDeviceId = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    addPlayer(player) {
      if (!player) return

      const exists = this.deviceFormData.players.some(p => p.id === player.id)

      if (!exists) {
        this.deviceFormData.players.push({
          id: player.id,
          name: player.name,
          priority: this.deviceFormData.players.length + 1
        })
      }

      this.selectedPlayer = null
    },
    removePlayer(index) {
      this.deviceFormData.players.splice(index, 1)
      this.updatePlayerOrder()
    },
    updatePlayerOrder() {
      this.deviceFormData.players.forEach((player, index) => {
        player.priority = index + 1
      })
    },
    getPlayerName(playerId) {
      const player = this.players.data.find(p => p.id === playerId)
      return player ? player.name : `Player ${playerId}`
    }
  }
}
</script>
